import { Chip, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import PropTypes from 'prop-types';

import {
  FILL_LATER_STATUS_LABEL,
  FILL_LATER_STATUS_TOOLTIP,
} from '../constants/fillLaterEligibility';

// The theme maps `warning.main` to the perfectRx navy, so the amber tint is built from
// `warning.warningColour` and paired with a darker amber that stays legible on it.
const AMBER_TEXT = '#8A5A00';

// Soft-tint treatment: each status keeps its hue, but as a light wash (12%, the same strength
// MUI's standard Alert uses) behind text of the same hue. Nine filled chips in a column fought
// for attention; the tints read at a glance without shouting.
const STATUS_TONE = {
  SCHEDULED: (theme) => ({
    bg: alpha(theme.palette.info.main, 0.12),
    fg: theme.palette.info.dark,
  }),
  WAITING_SHIPMENT: (theme) => ({
    bg: alpha(theme.palette.warning.warningColour, 0.18),
    fg: AMBER_TEXT,
  }),
  CREATED: (theme) => ({
    bg: alpha(theme.palette.success.main, 0.12),
    fg: theme.palette.success.main,
  }),
  FAILED: (theme) => ({
    bg: alpha(theme.palette.error.main, 0.12),
    fg: theme.palette.error.main,
  }),
  // Closed by an operator: the quantity went back onto the parent order (POM-6182).
  MERGED: (theme) => ({
    bg: theme.palette.grey[200],
    fg: theme.palette.text.secondary,
  }),
  // POM-6261: closed with the work it belonged to. Grey rather than red - a cancel is a routine
  // operator action, not a failure - and the tooltip carries what separates it from MERGED.
  CANCELLED: (theme) => ({
    bg: theme.palette.grey[200],
    fg: theme.palette.text.secondary,
  }),
};

// Unknown statuses fall back to the neutral grey tone, mirroring the raw-label fallback.
const toneFor = (status, theme) => (STATUS_TONE[status] ?? STATUS_TONE.MERGED)(theme);

/**
 * WAITING_SHIPMENT must still read as clearly different from SCHEDULED (POM-6149 AC2), so it
 * keeps the hourglass icon on top of its amber tint.
 */
const FillLaterQueueStatusChip = ({ status }) => {
  const tooltip = FILL_LATER_STATUS_TOOLTIP[status];

  const chip = (
    <Chip
      label={FILL_LATER_STATUS_LABEL[status] ?? status}
      size='small'
      data-testid={`fill-later-status-${status}`}
      icon={status === 'WAITING_SHIPMENT' ? <HourglassBottomIcon /> : undefined}
      sx={(theme) => {
        const { bg, fg } = toneFor(status, theme);
        return {
          fontWeight: 500,
          bgcolor: bg,
          color: fg,
          '& .MuiChip-icon': { color: fg },
        };
      }}
    />
  );

  return tooltip ? (
    <Tooltip title={tooltip} arrow>
      {chip}
    </Tooltip>
  ) : (
    chip
  );
};

FillLaterQueueStatusChip.propTypes = {
  status: PropTypes.string.isRequired,
};

export default FillLaterQueueStatusChip;
